import { useState, useEffect, useCallback } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import Card, { CardHeader, CardBody } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import LoadingOverlay, { WidgetWrapper } from '@/components/ui/LoadingOverlay';
import { apiClient } from '@/lib/api';
import type {
  CombinedStats,
  HealthCheck,
  TTSWorkerHealth,
  TTSJob,
  ElevenLabsDependencyHealth,
  TTSSynthesizerHealth,
} from '@/types/api';
import clsx from 'clsx';

function getStatusColor(status: string) {
  switch (status) {
    case 'ok':
    case 'healthy':
    case 'connected':
      return 'bg-success-500';
    case 'degraded':
      return 'bg-warning-500';
    case 'down':
    case 'unhealthy':
    case 'error':
    case 'disconnected':
      return 'bg-danger-500';
    default:
      return 'bg-slate-400';
  }
}

export default function TTSHealthPage() {
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Real data state
  const [stats, setStats] = useState<CombinedStats | null>(null);
  const [_health, setHealth] = useState<HealthCheck | null>(null);
  const [workerHealth, setWorkerHealth] = useState<TTSWorkerHealth | null>(null);
  const [elevenLabsHealth, setElevenLabsHealth] = useState<ElevenLabsDependencyHealth | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [recentActivity, setRecentActivity] = useState<Array<{
    time: string;
    jobId: string;
    event: string;
    message: string;
  }>>([]);

  // Failed/stuck jobs for alert widget
  const [failedJobs, setFailedJobs] = useState<TTSJob[]>([]);

  // Fetch health status from backend
  const fetchHealth = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) setIsLoading(true);
      else setIsRefreshing(true);

      // Fetch unified API health check
      try {
        const healthResponse = await apiClient.get<HealthCheck>('public/health');
        setHealth(healthResponse);
      } catch {
        // Health endpoint might not be available
        setHealth({
          status: 'degraded',
          checks: {
            api: 'error',
            tts_worker: 'unknown',
            distribution_worker: 'unknown',
            db: 'unknown',
          },
          timestamp: new Date().toISOString(),
        });
      }

      // Fetch TTS worker health for infrastructure services (R2, D1, KV)
      try {
        const workerHealthResponse = await apiClient.get<TTSWorkerHealth>('tts/health');
        setWorkerHealth(workerHealthResponse);
      } catch (err) {
        console.error('Failed to fetch TTS worker health:', err);
        // If this fails, fall back to marking infra services as unknown
        setWorkerHealth(null);
      }

      // Fetch ElevenLabs health from TTS Synthesizer Lambda
      try {
        const synthesizerHealth = await apiClient.get<TTSSynthesizerHealth>('tts/elevenlabs/health');
        const dependency = synthesizerHealth.dependencies?.elevenlabs ?? null;
        setElevenLabsHealth(dependency);
      } catch (err) {
        console.error('Failed to fetch ElevenLabs health:', err);
        setElevenLabsHealth(null);
      }

      // Fetch stats for queue info
      const statsResponse = await apiClient.get<CombinedStats>('tts/queue/stats');
      setStats(statsResponse);

      // Fetch recent jobs and surface failed ones as alerts
      try {
        const jobs = await apiClient.get<TTSJob[]>('tts/jobs?limit=50');
        const failed = jobs.filter((job) => job.status === 'failed');
        setFailedJobs(failed.slice(0, 5));
      } catch (err) {
        console.error('Failed to fetch failed jobs:', err);
        setFailedJobs([]);
      }
      setError(null);
    } catch (err) {
      console.error('Failed to fetch health data:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch health data');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  // Generate simulated activity based on real stats
  useEffect(() => {
    if (stats) {
      const activities = [
        { time: new Date().toLocaleTimeString(), jobId: 'system', event: 'health_check', message: `System status: ${stats.system_status}` },
      ];
      if (stats.queue_stats.processing_jobs > 0) {
        activities.push({ time: new Date().toLocaleTimeString(), jobId: 'queue', event: 'processing', message: `${stats.queue_stats.processing_jobs} jobs processing` });
      }
      if (stats.queue_stats.pending_jobs > 0) {
        activities.push({ time: new Date().toLocaleTimeString(), jobId: 'queue', event: 'pending', message: `${stats.queue_stats.pending_jobs} jobs pending` });
      }
      setRecentActivity(activities);
    }
  }, [stats]);

  // Initial data fetch
  useEffect(() => {
    fetchHealth();
  }, [fetchHealth]);

  // Auto-refresh every 5 seconds when enabled
  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(() => {
        fetchHealth(false);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [autoRefresh, fetchHealth]);

  // Build services array from stats and health
  const r2Service = workerHealth?.services?.r2_storage;
  const d1Service = workerHealth?.services?.d1_database;
  const kvService = workerHealth?.services?.kv_storage;
  const elevenLabsService = elevenLabsHealth;

  const services: Array<{
    name: string;
    status: string;
    icon: string;
    detail: string;
    isConnected: boolean;
  }> = [
    {
      name: 'TTS Worker',
      status: stats?.system_status === 'healthy' ? 'healthy' : stats?.system_status || 'unknown',
      icon: '⚡',
      detail: `${stats?.queue_stats.active_workers || 0} active`,
      isConnected: !!stats,
    },
    {
      name: 'Job Queue',
      status: stats ? 'healthy' : 'unknown',
      icon: '📋',
      detail: `${stats?.queue_stats.pending_jobs || 0} pending`,
      isConnected: !!stats,
    },
    {
      name: 'D1 Database',
      status: d1Service?.status || 'unknown',
      icon: '🗄️',
      detail: d1Service?.database || d1Service?.error || '--',
      isConnected: !!d1Service,
    },
    {
      name: 'KV Storage',
      status: kvService?.status || 'unknown',
      icon: '💾',
      detail: kvService?.namespaces?.join(', ') || kvService?.error || '--',
      isConnected: !!kvService,
    },
    {
      name: 'R2 Storage',
      status: r2Service?.status || 'unknown',
      icon: '📦',
      detail:
        r2Service
          ? `${r2Service.bucket ?? '--'}${
              typeof r2Service.objects_count === 'number'
                ? ` (${r2Service.objects_count} objects)`
                : ''
            }`
          : '--',
      isConnected: !!r2Service,
    },
    {
      name: 'ElevenLabs API',
      status:
        elevenLabsService?.status === 'ok'
          ? 'connected'
          : elevenLabsService?.status || 'unknown',
      icon: '🔊',
      detail:
        elevenLabsService
          ? `${elevenLabsService.endpoint ?? '--'}${
              typeof elevenLabsService.response_time_ms === 'number'
                ? ` (${elevenLabsService.response_time_ms} ms)`
                : ''
            }`
          : '--',
      isConnected: !!elevenLabsService && elevenLabsService.status === 'ok',
    },
  ];

  // Queue stats from real data
  const queueStats = {
    pending: stats?.queue_stats.pending_jobs ?? 0,
    running: stats?.queue_stats.processing_jobs ?? 0,
    completed: stats?.job_metrics.completed_jobs ?? 0,
    failed: stats?.job_metrics.failed_jobs ?? 0,
    total: stats?.job_metrics.total_jobs ?? 0,
    processingRate: '--', // NOT CONNECTED
    avgDuration: stats?.job_metrics.average_processing_time
      ? `${Math.round(stats.job_metrics.average_processing_time / 60000)}m`
      : '--',
    successRate: stats?.job_metrics.success_rate
      ? `${stats.job_metrics.success_rate.toFixed(1)}%`
      : '--',
  };

  const allHealthy = stats?.system_status === 'healthy';

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">System Health Dashboard</h1>
          <LoadingOverlay isLoading={isLoading} showSpinner={false} loadingOpacity={50}>
            <Badge variant={allHealthy ? 'success' : error ? 'danger' : 'warning'} size="lg">
              {allHealthy ? '🟢 All Systems Operational' : error ? '🔴 Connection Error' : '🟡 Some Issues Detected'}
            </Badge>
          </LoadingOverlay>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-600 dark:text-slate-300">Auto-refresh:</span>
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={clsx(
              'px-3 py-1 rounded-lg text-sm font-medium transition-colors',
              autoRefresh
                ? 'bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-300'
                : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-200'
            )}
          >
            {autoRefresh ? 'ON' : 'OFF'}
          </button>
          <Button
            variant="outline"
            leftIcon={<ArrowPathIcon className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />}
            onClick={() => fetchHealth(false)}
            disabled={isRefreshing}
          >
            Refresh
          </Button>
        </div>
      </div>

      {/* Service Status Grid */}
      <LoadingOverlay isLoading={isLoading} loadingOpacity={40} showSpinner={false}>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {services.map((service) => (
            <div
              key={service.name}
              className={clsx(
                'transition-opacity',
                !service.isConnected && 'opacity-20'
              )}
            >
              <Card>
                <CardBody className="text-center">
                  <div className="text-3xl mb-2">{service.icon}</div>
                  <p className={clsx(
                    'font-medium mb-1',
                      service.isConnected ? 'text-slate-900 dark:text-slate-100' : 'text-red-500'
                  )}>
                    {service.name}
                  </p>
                  <div className="flex items-center justify-center gap-2">
                    <div className={clsx('w-2 h-2 rounded-full', getStatusColor(service.status))} />
                    <span className={clsx(
                      'text-sm capitalize',
                        service.isConnected ? 'text-slate-600 dark:text-slate-300' : 'text-red-500'
                    )}>
                      {service.isConnected ? service.status : 'Not Connected'}
                    </span>
                  </div>
                  <p className={clsx(
                    'text-xs mt-1',
                      service.isConnected ? 'text-slate-500 dark:text-slate-400' : 'text-red-400'
                  )}>
                    {service.detail}
                  </p>
                </CardBody>
              </Card>
            </div>
          ))}
        </div>
      </LoadingOverlay>

      {/* Queue Metrics - Connected to Real Data */}
      <LoadingOverlay isLoading={isLoading} loadingOpacity={40}>
        <Card>
          <CardHeader title="Queue Metrics" />
          <CardBody>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
              <div className="text-center p-4 bg-warning-50 dark:bg-warning-900/20 rounded-xl">
                <p className="text-3xl font-bold text-warning-600">{queueStats.pending}</p>
                <p className="text-sm text-slate-700 dark:text-slate-200">Pending</p>
              </div>
              <div className="text-center p-4 bg-primary-50 dark:bg-primary-900/20 rounded-xl">
                <p className="text-3xl font-bold text-primary-600">{queueStats.running}</p>
                <p className="text-sm text-slate-700 dark:text-slate-200">Running</p>
              </div>
              <div className="text-center p-4 bg-success-50 dark:bg-success-900/20 rounded-xl">
                <p className="text-3xl font-bold text-success-600">{queueStats.completed}</p>
                <p className="text-sm text-slate-700 dark:text-slate-200">Complete</p>
              </div>
              <div className="text-center p-4 bg-danger-50 dark:bg-danger-900/20 rounded-xl">
                <p className="text-3xl font-bold text-danger-600">{queueStats.failed}</p>
                <p className="text-sm text-slate-700 dark:text-slate-200">Failed</p>
              </div>
              <div className="text-center p-4 bg-slate-50 dark:bg-slate-800/60 rounded-xl">
                <p className="text-3xl font-bold text-slate-800 dark:text-slate-100">{queueStats.total}</p>
                <p className="text-sm text-slate-700 dark:text-slate-200">Total</p>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-3 gap-4 text-center">
              <div>
                <p className={clsx(
                  'text-lg font-semibold',
                  queueStats.processingRate === '--'
                    ? 'text-red-500 opacity-50'
                    : 'text-slate-900 dark:text-slate-100'
                )}>
                  {queueStats.processingRate}
                </p>
                <p className="text-sm text-slate-600 dark:text-slate-300">Processing Rate</p>
              </div>
              <div>
                <p className="text-lg font-semibold text-slate-900 dark:text-slate-100">{queueStats.avgDuration}</p>
                <p className="text-sm text-slate-600 dark:text-slate-300">Avg Duration</p>
              </div>
              <div>
                <p className="text-lg font-semibold text-success-600">{queueStats.successRate}</p>
                <p className="text-sm text-slate-600 dark:text-slate-300">Success Rate</p>
              </div>
            </div>
          </CardBody>
        </Card>
      </LoadingOverlay>

      {/* Failed / Stuck Job Alerts */}
      <WidgetWrapper isConnected={failedJobs.length > 0}>
        {failedJobs.length > 0 && (
          <Card className="border-warning-200 bg-warning-50 dark:border-warning-800/40 dark:bg-warning-900/10">
            <CardHeader title="⚠️ Stuck Job Alerts" />
            <CardBody>
              <div className="space-y-4">
                {failedJobs.map((job) => (
                  <div
                    key={job.job_id}
                    className="flex items-center justify-between p-4 bg-white dark:bg-slate-900/60 rounded-xl border border-warning-200 dark:border-warning-800/40"
                  >
                    <div>
                      <p className="font-medium text-slate-900 dark:text-slate-100">
                        {job.metadata
                          ? (() => {
                              try {
                                const parsed = JSON.parse(job.metadata) as { pdf_path?: string; job_id?: string };
                                if (parsed.pdf_path) {
                                  const parts = parsed.pdf_path.split('/');
                                  return parts[parts.length - 1] || job.job_id;
                                }
                                return parsed.job_id || job.job_id;
                              } catch {
                                return job.job_id;
                              }
                            })()
                          : job.job_id}
                      </p>
                      <p className="text-sm text-slate-600 dark:text-slate-300">
                        {job.job_id} • Status: failed
                        {job.status_message ? ` • ${job.status_message}` : ''}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Last activity:{' '}
                        {job.updated_at
                          ? new Date(job.updated_at).toLocaleString()
                          : 'Unknown'}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Link to={`/tts/jobs/${encodeURIComponent(job.job_id)}`}>
                        <Button variant="outline" size="sm">
                          View Details
                        </Button>
                      </Link>
                      {/* Retry/Cancel actions can be wired up to real APIs later */}
                    </div>
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>
        )}
      </WidgetWrapper>

      {/* Recent Activity - Partially Connected */}
      <Card>
        <CardHeader title="Recent Activity (Live)" />
        <CardBody>
          {error ? (
            <div className="text-center py-8">
              <p className="text-red-500">{error}</p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => fetchHealth()}
              >
                Retry Connection
              </Button>
            </div>
          ) : recentActivity.length === 0 ? (
            <div className="text-center py-8 text-slate-600 dark:text-slate-300">
              No recent activity
            </div>
          ) : (
            <div className="space-y-2">
              {recentActivity.map((activity, index) => (
                <div
                  key={index}
                  className="flex items-center gap-4 py-2 border-b border-slate-50 dark:border-slate-800 last:border-0"
                >
                  <span className="font-mono text-sm text-slate-400 dark:text-slate-500 w-20">{activity.time}</span>
                  <span className="font-mono text-xs text-slate-500 dark:text-slate-400 w-24">{activity.jobId}</span>
                  <span className="text-slate-700 dark:text-slate-200">{activity.message}</span>
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
